import React, { useState, useEffect } from 'react';

import {
  Box,
  Card,
  Button,
  TextField,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Switch,
  Typography,
  IconButton
} from '@mui/material';

import { DatePicker } from '@mui/x-date-pickers'

import DeleteIcon from '@mui/icons-material/Delete';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import QRCode from 'react-qr-code';

import { fetchProposalEncrypted } from '../../api/Fetchers/fetch.licenseKey';
import { setSubmission } from '../../api/Setters/set.licenseKey';

/**
 * @typedef {string} TypeUsername
 */

/**
 * @typedef {Object} TypeDateOptional
 * @property {boolean} e
 * @property {string} v
 */

/**
 * @typedef {Object} TypeOptionalInt
 * @property {boolean} e
 * @property {number} v
 */

/**
 * @typedef {Object.<TypeUsername, string>} TypeUsersExpiryOptional
 */

/**
 * @typedef {Object} TypeOptions
 * @property {TypeDateOptional} exp
 * @property {TypeOptionalInt} ulm
 * @property {TypeUsersExpiryOptional} uex
 */

/**
 * @typedef {Object} TypeSubmission
 * @property {{opt: TypeOptions}} data
 * @property {string} sig
 */

/**
 * @typedef {Object} TypeProposal
 * @property {{idj: string, opt: TypeOptions}} data
 */

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#0d65d9',
      contrastText: '#fff',
    },
    background: {
      default: '#000000',
      paper: '#121212',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          padding: '12px 24px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: '#1e1e1e',
          },
        },
      },
    },
  },
});

export default function PageLicenseKey() {
  /**
   * @type {[TypeProposal, React.Dispatch<TypeProposal>]} state
   */
  const [proposal, setProposal] = useState(null);

  const [signature, setSignature] = useState('');

  const [expiryEnabled, setExpiryEnabled] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');

  const [limitEnabled, setLimitEnabled] = useState(false);

  const [userLimit, setUserLimit] = useState(0);

  const [usersExpiryList, setUsersExpiryList] = useState([]);

  const [usersExpiry, setUsersExpiry] = useState({});

  useEffect(() => {
    if (usersExpiryList.length > 0) {
      const filteredUsersExpiry = usersExpiryList.filter(item =>
        item.username && item.date && !isNaN(new Date(item.date).getTime())
      );

      setUsersExpiry(
        Object.fromEntries(
          filteredUsersExpiry.map(item => [item.username, item.date])
        )
      );
    }
  } , [usersExpiryList]);

  useEffect(() => {
    fetchProposalEncrypted(expiryEnabled, expiryDate, limitEnabled, userLimit, usersExpiry).then((res) => { console.log(res); setProposal(res.value) });
  }, [expiryEnabled, expiryDate, limitEnabled, userLimit, usersExpiry]);

  if (!proposal) return null;
  const proposalJsonString = JSON.stringify(proposal);

  const updateExpiry = (value) => {
    if (value) {
      const date = new Date(value);
      date.setUTCHours(23, 59, 59, 0);
      setExpiryDate(date.toISOString());
    } else {
      setExpiryDate('');
    }
  };

  const addUserExpiry = () => {
    setUsersExpiryList([...usersExpiryList, { username: '', date: '' }]);
  };

  const removeUserExpiry = (index) => {
    setUsersExpiryList(usersExpiryList.filter((_, i) => i !== index));
  };

  const updateUserExpiry = (index, field, value) => {
    if (field === 'date' && value) {
      const date = new Date(value);

      date.setUTCHours(23);
      date.setUTCMinutes(59);
      date.setUTCSeconds(59);

      value = date.toISOString();
    }
    setUsersExpiryList(usersExpiryList.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const submit = (value) => {
    console.log("proposal:", proposal)

    /** @type {TypeSubmission} */
    let submission = {};

    submission.data = {};
    submission.data.opt = proposal.data.opt

    submission.sig = signature

    setSubmission(submission)
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: 3,
          gap: 2,
          backgroundColor: '#000000',
          maxWidth: 800,
          margin: '0 auto',
        }}
      >
        <Card sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => window.history.back()}
            fullWidth
            sx={{ mb: 1, borderColor: 'rgba(255, 255, 255, 0.3)', color: '#fff' }}
          >
            Back
          </Button>
        </Card>
        {/* <Card sx={{ p: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'text.secondary',
              fontWeight: 600,
              mb: 2,
              pb: 1,
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            Current License Key
          </Typography>

          <Button
            variant="contained"
            fullWidth
            sx={{ backgroundColor: '#e51937', '&:hover': { backgroundColor: '#c4152c' } }}
          >
            Emergency Extend Current License Key
          </Button>
        </Card>*/}

        <Card sx={{ p: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'text.secondary',
              fontWeight: 600,
              mb: 2,
              pb: 1,
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            New License Key
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Switch
                checked={expiryEnabled}
                onChange={(e) => setExpiryEnabled(e.target.checked)}
              />
              <Typography>Expiry</Typography>
            </Box>
            {expiryEnabled && (
              <LocalizationProvider dateAdapter={AdapterLuxon}>
                <DatePicker
                  format="dd MMMM yyyy"
                  fullWidth
                  timezone="UTC"
                  onChange={(value) => {
                    if (value) {
                      const date = new Date(value);

                      date.setUTCHours(23, 59, 59, 0);

                      updateExpiry(date.toISOString());
                    } else {
                      updateExpiry('');
                    }
                  }}
                  slotProps={{
                    textField: {
                      InputLabelProps: { shrink: true },
                      sx: { mb: 2 }
                    }
                  }}
                />
              </LocalizationProvider>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Switch
                checked={limitEnabled}
                onChange={(e) => setLimitEnabled(e.target.checked)}
              />
              <Typography>User's Limit</Typography>
            </Box>
            {limitEnabled && (
              <TextField
                type="number"
                fullWidth
                value={userLimit}
                onChange={(e) => setUserLimit(Math.max(0, parseInt(e.target.value) || 0))}
                inputProps={{ min: 0 }}
                sx={{ mb: 2 }}
              />
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ mb: 1 }}>User's Expiry</Typography>
            <Button
              variant="outlined"
              onClick={addUserExpiry}
              fullWidth
              sx={{ mb: 1, borderColor: 'rgba(255, 255, 255, 0.3)', color: '#fff' }}
            >
              Add User
            </Button>
            {usersExpiryList.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  value={item.username}
                  onChange={(e) => updateUserExpiry(index, 'username', e.target.value)}
                  fullWidth
                  sx={{ flex: 1, backgroundColor: '#1e1e1e' }}
                />
                {/* <TextField
                  type="date"
                  lang="en-GB"
                  value={item.date ? new Date(item.date).toISOString().split('T')[0] : ''}
                  onChange={(e) => updateUserExpiry(index, 'date', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />*/}
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                  <DatePicker
                    format="dd MMMM yyyy"
                    timezone="UTC"
                    fullWidth
                    onChange={(value) => updateUserExpiry(index, 'date', value)}
                    slotProps={{
                      textField: {
                        InputLabelProps: { shrink: true },
                        sx: { mb: 2 }
                      }
                    }}
                  />
                </LocalizationProvider>
                <IconButton onClick={() => removeUserExpiry(index)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>

          <hr></hr>
          <br></br>

          <Box sx={{
            background: '#fff',
            p: 2,
            borderRadius: 2,
            mb: 2,
            display: 'flex',
            justifyContent: 'center'
          }}>
            <QRCode
              value={proposalJsonString}
              viewBox="0 0 256 256"
              style={{ width: "100%", height: "auto", maxHeight: "80vh" }}
            />
          </Box>

          <TextField
            multiline
            rows={4}
            value={proposalJsonString}
            disabled
            fullWidth
            sx={{ mb: 2 }}
          />

          <TextField
            label="Signature"
            variant="outlined"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            fullWidth
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              }
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => submit()}
            fullWidth
            sx={{ borderRadius: '12px' }}
          >
            Submit New License Key
          </Button>
        </Card>
      </Box>
    </ThemeProvider>
  );
}
